import { beginNavigationEpoch, NAVIGATION_RECOLLECT_DELAY_MS } from './navigation-epoch.js';

export type PageNavigationCallback = (url: string) => void;

export type WatchPageNavigationOptions = {
  /** Debounce for MutationObserver re-parse on same URL (SPA content swap). */
  mutationDebounceMs?: number;
  /** Called when the document URL changes (`popstate`, `pushState`, `replaceState`). */
  onNavigate: PageNavigationCallback;
  /**
   * Optional: debounced callback when DOM changes but URL stays the same
   * (soft SPA content updates). Omit when only URL changes matter.
   */
  onContentChange?: PageNavigationCallback;
};

const DEFAULT_MUTATION_DEBOUNCE_MS = NAVIGATION_RECOLLECT_DELAY_MS;

type Subscriber = {
  onNavigate: PageNavigationCallback;
  onContentChange?: PageNavigationCallback;
  mutationDebounceMs: number;
};

/** Insertion order = notify order (first subscriber runs first on navigate). */
const subscribers = new Set<Subscriber>();

let patched = false;
let lastUrl = '';
let mutationTimer: ReturnType<typeof setTimeout> | null = null;
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;
let observer: MutationObserver | null = null;

function notifyUrlChange(): void {
  if (typeof window === 'undefined') return;
  const nextUrl = window.location.href;
  if (nextUrl === lastUrl) return;
  lastUrl = nextUrl;
  beginNavigationEpoch(nextUrl);
  for (const sub of subscribers) {
    sub.onNavigate(nextUrl);
  }
}

function notifySameUrlContentChange(): void {
  if (typeof window === 'undefined') return;
  if (![...subscribers].some((s) => s.onContentChange)) return;

  const debounceMs = Math.min(
    ...[...subscribers].map((s) => s.mutationDebounceMs),
  );

  if (mutationTimer) clearTimeout(mutationTimer);
  mutationTimer = setTimeout(() => {
    const url = window.location.href;
    for (const sub of subscribers) {
      sub.onContentChange?.(url);
    }
  }, debounceMs);
}

function onPopState(): void {
  notifyUrlChange();
}

function ensurePatched(): void {
  if (typeof window === 'undefined' || patched) return;

  patched = true;
  lastUrl = window.location.href;
  beginNavigationEpoch(lastUrl);

  window.addEventListener('popstate', onPopState);

  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args) => {
    originalPushState!(...args);
    notifyUrlChange();
  };

  history.replaceState = (...args) => {
    originalReplaceState!(...args);
    notifyUrlChange();
  };

  observer = new MutationObserver(() => {
    notifySameUrlContentChange();
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function maybeUnpatch(): void {
  if (typeof window === 'undefined' || subscribers.size > 0 || !patched) return;

  patched = false;
  window.removeEventListener('popstate', onPopState);
  if (originalPushState) history.pushState = originalPushState;
  if (originalReplaceState) history.replaceState = originalReplaceState;
  originalPushState = null;
  originalReplaceState = null;
  observer?.disconnect();
  observer = null;
  if (mutationTimer) {
    clearTimeout(mutationTimer);
    mutationTimer = null;
  }
}

/**
 * Track SPA navigations: `popstate`, patched `history.pushState` / `replaceState`,
 * and optional debounced DOM mutations when the URL stays the same.
 *
 * Multiple callers share one history patch; subscribers are notified in
 * registration order so early installs (e.g. page-visit analytics) run before
 * later ones (e.g. behavior reset).
 */
export function watchPageNavigation(
  options: WatchPageNavigationOptions,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const subscriber: Subscriber = {
    onNavigate: options.onNavigate,
    onContentChange: options.onContentChange,
    mutationDebounceMs: options.mutationDebounceMs ?? DEFAULT_MUTATION_DEBOUNCE_MS,
  };

  subscribers.add(subscriber);
  ensurePatched();

  return () => {
    subscribers.delete(subscriber);
    maybeUnpatch();
  };
}

/** Test helper — clear shared navigation hub state. */
export function resetPageNavigationWatchersForTests(): void {
  subscribers.clear();
  maybeUnpatch();
  lastUrl = typeof window !== 'undefined' ? window.location.href : '';
}
